import { mapState } from 'vuex';
import { clearAnswer } from '@assessment/../api/answer';

export default {
  name: 'BaseAnswers',

  props: {
    currentEmail: {
      type: Object,
      required: true,
    },
    enableAutoSave: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  created() {
    // this.picked = this.currentEmail?.answer[0]?.answerKey
    this.reset();
  },

  data() {
    return {
      picked: [],
      smartAnswer: null,
      writtenResponse: '',
      isSaving: false,
    };
  },

  render() {
    return this.$scopedSlots.default({
      submit: this.submitAnswer,
      isComplete: this.isComplete,
      isWrittenResponse: this.isWrittenResponse,
      answers: this.answersToShow,
      pickedAnswers: this.picked,
      submittedAnswers: this.submittedAnswers,
      submittedSmartAnswer: this.smartAnswer,
      hasAnswersToFill: this.hasAnswersToFill,
      hasValidAnswer: this.hasValidAnswer,
      isSaving: this.isSaving,
      // Aggregated element props. Use these on answer inputs to sync form data with the state here in the base component
      answerProps: {
        written: {
          value: this.writtenResponse,
        },
      },

      // Aggregated event listeners. Use these on answer inputs to pass the user select event here
      answerEvents: {
        single: {
          change: (e) => {
            this.picked = Number(e.target.value);
            if (this.enableAutoSave) this.submitAnswer();
          },
        },
        multi: {
          change: (e) => {
            const pickedAnswerKey = Number(e.target.value);
            if (e.target.checked) {
              this.picked.push(pickedAnswerKey);
            } else {
              this.picked = this.picked.filter((answerKey) => answerKey !== pickedAnswerKey);
            }
            if (this.enableAutoSave) this.submitAnswer();
          },
        },
        written: {
          input: (value) => {
            this.writtenResponse = value;
          },
        },
      },
    });
  },

  watch: {
    'currentEmail.questionKey': function () {
      this.reset();
    },
  },
  computed: {
    ...mapState(['theme', 'assessmentTypeKey', 'versionTemplateKey']),

    isComplete() {
      return this.isSent || this.smartAnswer;
    },

    answerPicked() {
      return this.currentEmail.answer || {};
    },

    answersToShow() {
      const { answers, isSmartThreading, FK_questionTypeKey, smartThreadingSelectedAnswers } =
        this.currentEmail;
      let answersToShow = answers;
      if (isSmartThreading == 1 && FK_questionTypeKey !== 2) {
        let answeredEmailKeyArr = smartThreadingSelectedAnswers || [];
        answersToShow = answersToShow.filter((e) => answeredEmailKeyArr.indexOf(e.answerKey) == -1);
      }
      return answersToShow.map((answer) => ({
        ...answer,
        nameTagKey: this.parseAnswer(answer.nameTagKey),
      }));
    },

    isSent() {
      return this.currentEmail.isSent || false;
    },

    isWrittenResponse() {
      return (
        this.currentEmail.isWrittenResponse === 1 || this.currentEmail.FK_questionTypeKey === 3
      );
    },

    submittedAnswers() {
      return Object.values(this.answerPicked).map((ans) => this.parseAnswer(ans.nameTagKey));
    },

    hasAnswersToFill() {
      return this.currentEmail.answers.length > 0 || this.isWrittenResponse;
    },
    hasValidAnswer() {
      if (this.currentEmail.FK_questionDisplayTypeKey === 3) {
        return true;
      }
      if (this.isWrittenResponse) {
        return !!this.writtenResponse.length;
      }
      return Array.isArray(this.picked) ? !!this.picked.length : true;
    },
  },
  methods: {
    filterAnswers(answerPicked) {
      const result = this.currentEmail.answers.filter((answer) => {
        return answer.answerKey == answerPicked.answerKey;
      });
      return result.map((x) => this.parseAnswer(x.nameTagKey)).join(', ');
    },

    formatDate(match, format) {
      let offset = parseInt(match.substring(8, match.length - 2));
      return this.$dayjs()
        .startOf('week')
        .subtract(2 + offset, 'day')
        .format(format);
    },

    parseAnswer(res) {
      return res
        .replace(/{name}/g, this.authorFirstName)
        .replace(/{date.\(-\d{0,2}\)}/g, (match) => this.formatDate(match, 'MMMM D, YYYY'))
        .replace(/{date}/g, this.$dayjs().startOf('week').subtract(2, 'day').format('MMMM D, YYYY'))
        .replace(/{datetime.\(-\d{0,2}\)}/g, (match) =>
          this.formatDate(match, 'MMMM D, YYYY h:mm a')
        )
        .replace(
          /{datetime}/g,
          this.$dayjs().startOf('week').subtract(2, 'day').format('MMMM D, YYYY h:mm a')
        );
    },

    reset() {
      this.picked = [];
      this.writtenResponse = '';
      this.smartAnswer = null;

      if (this.currentEmail.writtenResponse) {
        this.writtenResponse = this.currentEmail.writtenResponse;
      }

      if ('answer' in this.currentEmail) {
        if (this.currentEmail.FK_questionTypeKey == 1) {
          this.picked = this.currentEmail.answer[0].answerKey;
        } else {
          for (let i = 0; i < this.currentEmail.answer.length; i++) {
            this.picked.push(this.currentEmail.answer[i].answerKey);
          }
        }
      }
    },

    async submitAnswer() {
      if (this.currentEmail.FK_questionDisplayTypeKey === 3) {
        const feedbackAnswerKey = Object.keys(this.currentEmail.answers).filter((answerKey) => {
          return this.currentEmail.answers[answerKey].answerTypeKey === 3;
        })[0];
        this.picked = this.currentEmail.answers[feedbackAnswerKey].answerKey;
      }
      if (!this.isWrittenResponse && this.currentEmail.FK_questionDisplayTypeKey !== 3) {
        if (!this.validate()) return;
      }
      this.isSaving = true;

      location.href = '#app';

      try {
        // Clears answers before inserting new answers
        if (this.currentEmail.isSent && this.assessmentTypeKey === 1) {
          const answerKeys = this.answerPicked.map((x) => x.answerKey);
          await clearAnswer({
            questionkey: this.currentEmail.questionKey,
            oldAnswerkey: answerKeys,
          });
        }

        // Inserting answers
        if (this.currentEmail.isWrittenResponse) {
          await this.$store.dispatch('email/ANSWER_EMAIL_WRITTEN', {
            questionkey: this.currentEmail.questionKey,
            writtenResponse: this.writtenResponse,
          });
        } else if (this.currentEmail.FK_questionTypeKey === 3) {
          await this.$store.dispatch('email/ANSWER_EMAIL', {
            questionkey: this.currentEmail.questionKey,
            answerkey: this.currentEmail.answers[0].answerKey,
            writtenResponse: this.writtenResponse,
            isSmartThreading: false,
          });
        } else {
          if (this.currentEmail.isSmartThreading) {
            this.$store.commit('SET_STATE', {
              data: { recentlySelectedAnswerKey: this.picked },
            });
          }

          await this.$store.dispatch('email/ANSWER_EMAIL', {
            questionkey: this.currentEmail.questionKey,
            answerkey: Array.isArray(this.picked) ? this.picked : Number(this.picked),
            isSmartThreading: this.currentEmail.isSmartThreading,
          });
        }
        if (this.currentEmail.isWrittenResponse || this.currentEmail.FK_questionTypeKey === 3)
          return;

        // Calculate time impact if any
        const question = this.$store.state.emails.find((question) => {
          return question.questionKey === this.currentEmail.questionKey;
        });

        let answers = typeof this.picked == 'object' ? this.picked : [this.picked];
        let questionAnswers = question.answers.filter((answer) =>
          answers.includes(answer.answerKey)
        );
        let timeImpact = 0;
        questionAnswers.forEach((answer) => {
          timeImpact += answer.timeImpact;
        });
        if (timeImpact != 0) this.$store.commit('time/UPDATE_DEADLINE', timeImpact);

        if (this.currentEmail.isSmartThreading) {
          this.smartAnswer = this.parseAnswer(questionAnswers[0].nameTagKey);
        }
      } catch (e) {
        this.$store.dispatch('Errors/responseError', e);
      } finally {
        setTimeout(() => {
          this.isSaving = false;
        }, 500);
      }
    },

    validate() {
      if (!(Array.isArray(this.picked) && this.picked.length == 0)) return true;
      return false;
    },
  },
};
